import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, CheckCheck, AlertCircle, Wrench, Package } from "lucide-react";

const ICONS = {
  os_created: AlertCircle,
  preventive_due: Wrench,
  part_request: Package,
  low_stock: Package,
};

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = useCallback(
    () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}),
    [],
  );

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openNotif = async (n) => {
    setOpen(false);
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    if (n.kind === "os_created" && n.ref_id) nav(`/os/${n.ref_id}`);
    if (n.kind === "preventive_due") nav("/preventiva");
    if (n.kind === "part_request" || n.kind === "low_stock") nav("/almoxarifado");
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="rounded-sm relative"
          data-testid="notification-bell"
        >
          <Bell className="w-4 h-4" />
          {data.unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-sm">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Central</div>
            <div className="font-heading font-bold">Notificações</div>
          </div>
          {data.unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} className="rounded-sm gap-1.5" data-testid="mark-all-read">
              <CheckCheck className="w-3.5 h-3.5" /> Marcar todas
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Sem notificações</div>
          ) : (
            <div>
              {data.items.map((n) => {
                const Icon = ICONS[n.kind] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => openNotif(n)}
                    data-testid={`notification-${n.id}`}
                    className={`w-full text-left px-4 py-3 border-b flex gap-3 hover:bg-secondary transition-colors ${!n.read ? "bg-orange-50 dark:bg-orange-500/5" : ""}`}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-sm flex items-center justify-center ${!n.read ? "bg-orange-500 text-white" : "bg-secondary text-muted-foreground"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{n.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{fmtDate(n.created_at)}</div>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-orange-500 shrink-0 mt-1.5" />}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
